import { ClusterLayer, VectorLayer } from '@datum-group/datum-map/lib';
import { APITableLayer } from '../shared/RecordCard/types';
import { computed, makeObservable, observable } from 'mobx';
import { TableCollection } from '../TableCollection';
import { MapStore } from '../Map';
import { MapMarkerFacade } from '../MapMarker';
import {
  ISingleUnwrappedWindow,
  SingleUnwrappedWindow,
  WindowState,
} from '../Window';
import { LayerFilterController } from './LayerFilterController';

export interface WindowContentData {
  // TODO:
  // Данные всех запрошенных слоёв инкапсулированы внутри layerTree.
  // Необходимо создать сущность, которая будет хранить запрошенные слои и в которой через id, можно будет получать данные слоя.
  // Сейчас приходится пробрасывать все используемые данные вот таким образом
  layer: APITableLayer;
}

export class LayerTableWindowsController
  implements ISingleUnwrappedWindow<WindowContentData>
{
  private _windowCollection: SingleUnwrappedWindow<WindowContentData>;
  private _tableCollection: TableCollection;
  private _map: MapStore;
  private _marker: MapMarkerFacade;
  private _markerId?: string;

  @observable private _layerController: Map<string, LayerFilterController>;

  constructor(map: MapStore, marker: MapMarkerFacade) {
    this._map = map;
    this._marker = marker;
    this._layerController = new Map();
    this._tableCollection = new TableCollection();
    this._windowCollection = new SingleUnwrappedWindow<WindowContentData>();

    makeObservable(this);
  }

  @computed get active() {
    return this._windowCollection.active;
  }

  @computed get layers() {
    const result: Record<string, LayerFilterController> = {};

    for (let [key, controller] of this._layerController.entries()) {
      result[key] = controller;
    }

    return result;
  }

  @computed get windows(): Record<string, WindowState<WindowContentData>> {
    return this._windowCollection.windows;
  }

  wrap = (layerName?: string) => {
    this._windowCollection.wrap(layerName);
  };

  unwrap = (layerName?: string) => {
    this._windowCollection.unwrap(layerName);
    this.hideMarker();
  };

  close = (layerName?: string) => {
    const id = layerName || this.active;

    if (!id) return;

    const tableId = this._windowCollection.windows[id]?.data.layer.data_source;

    if (tableId) {
      this._tableCollection.delete(tableId);
    }

    this._layerController.get(id)?.removeFilter();

    if (this._markerId === id) {
      this.hideMarker();
    }

    this._windowCollection.close(id);
  };

  open = async (layerName: string, data: WindowContentData) => {
    await this._tableCollection.set(data.layer.data_source);

    const tableController = this._tableCollection.list.get(
      data.layer.data_source,
    );

    if (!tableController) {
      const msg = `Не удалось получить таблицу - ${data.layer.data_source}`;

      throw new Error(msg);
    }

    const layerController = new LayerFilterController(
      this._map,
      tableController,
      data.layer,
    );

    this._layerController.set(layerName, layerController);

    this._windowCollection.open(layerName, data);

    this.hideMarker();
  };

  showMarker = (geometry: GeoJSON.Geometry) => {
    const id = this.active;

    if (!id) return;

    this._addLayer(id);

    this._markerId = id;
    this._marker.create(geometry, 'outlined');
    this._map.goToGeom(geometry);
  };

  hideMarker = () => {
    this._marker.remove();
    this._markerId = undefined;
  };

  private _addLayer = (layerName: string) => {
    const layerCollection = this._map.layerCollection;

    const datumLayer = layerCollection.getLayer(layerName);

    if (datumLayer) {
      if (
        !(
          datumLayer instanceof VectorLayer ||
          datumLayer instanceof ClusterLayer
        )
      ) {
        throw new Error(`Невалидный тип слоя ${layerName}`);
      }

      return datumLayer;
    }

    const apiLayer = this.windows[layerName]?.data.layer;

    if (!apiLayer) {
      throw new Error(`Не удалось получить данные слоя ${layerName}`);
    }

    const layer = layerCollection.add(apiLayer);

    if (!(layer instanceof VectorLayer || layer instanceof ClusterLayer)) {
      layerCollection.remove(layerName);

      throw new Error(`Невалидный тип слоя ${layerName}`);
    }

    return layer;
  };
}
